import { useEffect, useRef, useState } from 'react';
import { SPEECH_PER_RECORDING, type VoiceStatus } from '@arcnet/contracts';
import { getAccessToken } from '../../lib/api/client.js';
import { formatNumber } from '../../lib/format.js';
import { useMakeRecording, useVoice, useVoices } from '../../lib/api/hooks-voice.js';
import { Modal } from '../ui/Modal.js';
import { Icon } from '../ui/Icon.js';

/**
 * A recording made from text (owner, 7 October 2026): the words, a voice, and the file it makes,
 * which is an ordinary recording from then on. Used on the Media page and beside every recording
 * picker, so a step that needs a recording nobody has made can have one without leaving it.
 */

type Kind = 'prompt' | 'greeting' | 'announcement';
const KIND_LABEL: Record<Kind, string> = { greeting: 'Greeting', prompt: 'Prompt', announcement: 'Announcement' };

/** "4,180 of 20,000 characters this month on the platform's key." */
export function usageLine(s: VoiceStatus): string {
  if (!s.key) return '';
  const used = s.used[s.key];
  const cap = s.key === 'platform' ? s.caps.platform : s.caps.own;
  const whose = s.key === 'platform' ? 'the platform’s ElevenLabs key' : 'this account’s ElevenLabs key';
  return cap === null
    ? `${formatNumber(used)} characters made this month on ${whose}.`
    : `${formatNumber(used)} of ${formatNumber(cap)} characters this month on ${whose}.`;
}

/** Plays a voice's sample, fetched with the session (an <audio src> cannot send it). */
export function useVoicePreview(tenantId: string) {
  const [playing, setPlaying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const el = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => { el.current?.pause(); }, []);
  const play = async (voiceId: string) => {
    setError(null);
    el.current?.pause();
    if (playing === voiceId) { setPlaying(null); return; }
    const res = await fetch(`/api/v1/tenants/${tenantId}/voice/voices/${encodeURIComponent(voiceId)}/preview`, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!res.ok) { setError(`The sample could not be played (${res.status}).`); return; }
    const audio = new Audio(URL.createObjectURL(await res.blob()));
    el.current = audio;
    audio.onended = () => setPlaying(null);
    setPlaying(voiceId);
    await audio.play().catch(() => { setPlaying(null); setError('This browser could not play the sample.'); });
  };
  return { playing, error, play };
}

export function FromTextForm({ tenantId, kind: fixedKind, defaultName = '', defaultText = '', onMade, onCancel, idPrefix = 'ft' }: {
  tenantId: string;
  /** Fixed by where it is made (a menu's greeting, a message's prompt); chosen on the Media page. */
  kind?: Kind | undefined;
  defaultName?: string; defaultText?: string;
  onMade: (media: { id: string; name: string }) => void;
  onCancel?: (() => void) | undefined;
  idPrefix?: string;
}) {
  const status = useVoice(tenantId);
  const s = status.data;
  const voices = useVoices(tenantId, !!s?.key);
  const make = useMakeRecording(tenantId);
  const preview = useVoicePreview(tenantId);
  const [kind, setKind] = useState<Kind>(fixedKind ?? 'prompt');
  const [name, setName] = useState(defaultName);
  const [text, setText] = useState(defaultText);
  const [voiceId, setVoiceId] = useState<string>('');
  const voice = voiceId || s?.voice?.id || '';

  if (!s) return <div className="arc-skeleton" style={{ height: 120 }} />;
  if (!s.key) {
    return (
      <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>
        Making recordings from text needs an ElevenLabs key. Add one under Recordings from text on the Media page.
      </p>
    );
  }
  const chars = text.trim().length;
  const list = voices.data?.data ?? [];
  const chosen = list.find((v) => v.voice_id === voice);

  return (
    <form className="arc-stack" style={{ gap: 12 }} onSubmit={(e) => {
      e.preventDefault();
      // Opened from a step's editor, this form sits inside the step's own form in React's tree,
      // and a submit would carry on up to it and apply the step half-made.
      e.stopPropagation();
      make.mutate({
        kind, name: name.trim(), text: text.trim(),
        ...(voiceId && voiceId !== s.voice?.id ? { voice_id: voiceId, voice_name: chosen?.name ?? voiceId } : {}),
      }, { onSuccess: (m) => { setName(''); setText(''); onMade({ id: m.id, name: m.name }); } });
    }}>
      <div className="arc-grid arc-grid-2">
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor={`${idPrefix}-name`}>Name</label>
          <input id={`${idPrefix}-name`} className="arc-input" required maxLength={120} value={name}
            onChange={(e) => setName(e.target.value)} placeholder="After-hours notice" />
        </div>
        {fixedKind ? null : (
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor={`${idPrefix}-kind`}>Kind</label>
            <select id={`${idPrefix}-kind`} className="arc-select" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {(['greeting', 'prompt', 'announcement'] as const).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="arc-field" style={{ margin: 0 }}>
        <label className="arc-label" htmlFor={`${idPrefix}-text`}>What it says</label>
        <textarea id={`${idPrefix}-text`} className="arc-input" required rows={4} maxLength={SPEECH_PER_RECORDING} value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Thank you for calling. Our office is closed. We are open weekdays from eight to five." />
        <p className="arc-hint" style={{ display: 'flex', gap: 12 }}>
          <span style={{ flex: 1 }}>Written as it should be said: times and numbers the way a person says them.</span>
          <span className="arc-num" aria-live="polite">{formatNumber(chars)} of {formatNumber(SPEECH_PER_RECORDING)}</span>
        </p>
      </div>
      <div className="arc-field" style={{ margin: 0 }}>
        <label className="arc-label" htmlFor={`${idPrefix}-voice`}>Voice</label>
        <div className="arc-row" style={{ gap: 8 }}>
          <select id={`${idPrefix}-voice`} className="arc-select" style={{ flex: 1, minWidth: 0 }} value={voice}
            onChange={(e) => setVoiceId(e.target.value)} disabled={voices.isLoading}>
            {!voice && <option value="">Choose a voice…</option>}
            {s.voice && !list.some((v) => v.voice_id === s.voice!.id) && <option value={s.voice.id}>{s.voice.name}</option>}
            {list.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {v.name}{v.voice_id === s.voice?.id ? ' (house voice)' : ''}{[v.accent, v.gender].filter(Boolean).length ? ` · ${[v.accent, v.gender].filter(Boolean).join(', ')}` : ''}
              </option>
            ))}
          </select>
          <button type="button" className="arc-btn arc-btn-sm" disabled={!voice || (chosen ? !chosen.has_preview : false)}
            onClick={() => { void preview.play(voice); }}>
            <Icon name={preview.playing === voice ? 'pause' : 'play'} size={12} />{preview.playing === voice ? 'Stop' : 'Listen'}
          </button>
        </div>
        {voices.error && <p className="arc-error" style={{ margin: '4px 0 0' }}>{(voices.error as Error).message}</p>}
        {preview.error && <p className="arc-error" style={{ margin: '4px 0 0' }}>{preview.error}</p>}
      </div>
      {make.error && <p className="arc-error" style={{ margin: 0 }}>{(make.error as Error).message}</p>}
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button type="submit" className="arc-btn arc-btn-primary arc-btn-sm" disabled={make.isPending || chars === 0 || !voice || !name.trim()}>
          {make.isPending ? 'Making…' : 'Make recording'}
        </button>
        {onCancel && <button type="button" className="arc-btn arc-btn-sm" onClick={onCancel}>Cancel</button>}
        <span className="arc-subtle" style={{ fontSize: 12 }}>{usageLine(s)}</span>
      </div>
    </form>
  );
}

/**
 * Beside a recording picker: make one from text, then it is chosen. With `suggested`, the words the
 * AI flow builder wrote for this step are ready in it (docs/41 §12).
 */
export function MakeRecordingAction({ tenantId, kind, defaultName, suggested, onMade }: {
  tenantId: string; kind: Kind; defaultName: string; suggested?: string | undefined;
  onMade: (media: { id: string; name: string }) => void;
}) {
  const status = useVoice(tenantId);
  const [open, setOpen] = useState(false);
  if (!status.data?.key && !suggested) return null;
  return (
    <>
      {suggested ? (
        <div className="arc-make-suggest">
          <span className="arc-label" style={{ margin: 0 }}>Suggested words</span>
          <p>“{suggested}”</p>
          {status.data?.key
            ? <button type="button" className="arc-btn arc-btn-sm" onClick={() => setOpen(true)}><Icon name="speaker" size={12} />Make this recording</button>
            : <span className="arc-hint" style={{ margin: 0 }}>Record them and upload the file, or add an ElevenLabs key under Media to make it from these words.</span>}
        </div>
      ) : (
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ marginTop: 4 }} onClick={() => setOpen(true)}>
          <Icon name="speaker" size={12} />Make one from text
        </button>
      )}
      {open && (
        <Modal title="Make a recording from text" size="md" onClose={() => setOpen(false)}>
          <div className="arc-confirm">
            <FromTextForm tenantId={tenantId} kind={kind} defaultName={defaultName} defaultText={suggested ?? ''} idPrefix={`mr-${kind}`}
              onMade={(m) => { setOpen(false); onMade(m); }} onCancel={() => setOpen(false)} />
          </div>
        </Modal>
      )}
    </>
  );
}
